import log from "loglevel";
import { inject, injectable } from "tsyringe";
import { TOKEN_IProjectBus, type IProjectBus } from "./ProjectBus";


export const TOKEN_IWrapNavigation = Symbol('IWrapNavigation');

/*
 * Wrapping window.location logic, callback when user hits back in browser. Construction of UrlSearchParams
 */
export interface IWrapNavigation {
    /**
     * Navigates to the specified URL
     * @param to The URL to navigate to
     * @param silent Optional. If true, replaces current history state instead of pushing new one
     */
    navigateTo(to: string, silent?: boolean): void;

    get pathName(): string;

    get url(): string;

    get urlSearchParams(): URLSearchParams;
}

@injectable()
export class WrapNavigation implements IWrapNavigation {
    private _urlSearchParams: URLSearchParams | undefined;
    private _search: string = window.location.search;
    private _hash: string = window.location.hash;

    constructor(
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus
    ) {
        addEventListener("popstate", () => {
            this.projectBus.userHitBackButton();
        });
    }

    get url(): string {
        return window.location.toString()
    }

    get pathName(): string {
        return window.location.pathname;
    }

    get urlSearchParams(): URLSearchParams {
        if (this._urlSearchParams !== undefined && this._search === window.location.search && this._hash === window.location.hash) {
            return this._urlSearchParams;
        }

        this._urlSearchParams = this.createSearchParams();
        this._search = window.location.search;
        this._hash = window.location.hash;

        return this._urlSearchParams;
    }

    private createSearchParams(): URLSearchParams {
        const searchParams = new URLSearchParams(window.location.search);

        if (window.location.hash) {
            // Remove the # symbol and create new URLSearchParams from hash
            const hashParamsString = window.location.hash.substring(1);
            // Handle empty hash
            if (hashParamsString) {
                const hashParams = new URLSearchParams(
                    // Handle non-encoded hash params like #welcome by treating them as welcome=
                    hashParamsString.includes('=')
                        ? hashParamsString
                        : `${hashParamsString}=`
                );

                // Append all hash params to the search params
                hashParams.forEach((value, key) => {
                    searchParams.append(key, value);
                });
            }
        }

        return searchParams;
    }

    get currentUrl() {
        return window.location.pathname;
    }

    navigateTo = (to: string, silent: boolean = false) => {
        const url = new URL(to.startsWith('/') ? to : `/${to}`, location.origin);

        log.info(silent ? "navigateToSilent" : "navigateTo", to);

        const navigationMethod = silent ? history.replaceState : history.pushState;
        navigationMethod.call(history, null, "", url);
    }
}